import { useState } from "react";
import { Building2 } from "lucide-react";
import { Button } from "@altek/ui/components/ui/Button";
import type { Session } from "@/features/auth/api/session.api";
import { TenantPicker } from "./TenantPicker";

/**
 * Which customer am I in — kept on screen, for the people for whom the answer is not obvious.
 *
 * THE MISTAKE IT PREVENTS is the one the picker prevents at sign-in, an hour later. An
 * accountant who serves three small businesses chooses one at the start of the session and then
 * works for an hour; nothing on the page afterwards says which. Posting a document, adding an
 * employee or reading an audit log in the wrong customer's books is not recoverable by an undo,
 * and the system cannot detect it — every one of those actions is legitimate in the customer it
 * lands in.
 *
 * WHY IT IS NOT A DROPDOWN BESIDE THE COMPANY SELECTOR. The header's selector switches COMPANY
 * inside one customer — an everyday action, done several times a day by people who run two
 * legal entities. This is a different question with a different blast radius, and two dropdowns
 * side by side would teach people the two are the same kind of thing. So it sits above the
 * chrome, reads as a statement rather than a control, and its one action reopens the same
 * full-screen card that asked the question at sign-in.
 *
 * IT IS ABSENT FOR ALMOST EVERYBODY — one reachable customer means no strip at all
 * (`showsTenantContext`). A bar that says something obvious to 95% of users is a bar the other
 * 5% also stop reading.
 */
export function TenantBar({ session }: { session: Session }) {
  const [switching, setSwitching] = useState(false);
  const current = session.tenant.current;

  if (switching) {
    /**
     * The same card as sign-in, and deliberately the same: switching customer replaces the
     * permission set, the reachable companies and the enabled module list, so the application
     * behind it is about to mean something else. A dropdown that changed all of that without
     * taking over the screen would understate what just happened.
     */
    return <TenantPicker session={session} onCancel={() => setSwitching(false)} />;
  }

  return (
    <div className="flex items-center gap-2 border-b border-border-subtle bg-surface-2 px-4 py-1.5 text-xs">
      <Building2 aria-hidden className="size-3.5 shrink-0 text-fg-muted" />
      <span className="text-fg-muted">Müşteri:</span>
      <span className="truncate font-medium text-fg">
        {current ? (current.displayName ?? current.name) : "Seçilmedi"}
      </span>
      <Button
        variant="ghost"
        size="sm"
        // Telefonda 44 px (9 Ekim 2026, 5. tur): 24 px'lik düğme parmakla tutturulmuyordu.
        className="ml-auto h-6 px-2 text-xs max-md:h-11"
        onClick={() => setSwitching(true)}
      >
        Değiştir
      </Button>
    </div>
  );
}
